import React, { useState, useEffect } from 'react';
import { FaTimes, FaCheck } from 'react-icons/fa';

/**
 * Mobile-optimized filter view that displays filters in a modal/drawer
 * Works for both spaces and topics
 */
const MobileFilterView = ({ 
    isOpen, 
    onClose, 
    items = [], 
    selectedItems = [], 
    onApply,
    title = "Filters",
    type = "topics" // "topics" or "spaces"
}) => {
    const [localSelected, setLocalSelected] = useState(selectedItems);

    useEffect(() => {
        setLocalSelected(selectedItems);
    }, [selectedItems, isOpen]);

    const toggleSelection = (itemId) => {
        setLocalSelected((prev) => {
            const updatedSelection = prev.includes(itemId) 
                ? prev.filter(id => id !== itemId)
                : [...prev, itemId];
            
            // Apply immediately without requiring "Apply" button click
            onApply(updatedSelection);
            return updatedSelection;
        });
    };

    const handleClear = () => {
        setLocalSelected([]);
        onApply([]);
    };

    if (!isOpen) return null;

    return (
        <>
            <style>
                {`
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    @keyframes slideUp {
                        from { 
                            transform: translateY(100%); 
                        }
                        to { 
                            transform: translateY(0); 
                        }
                    }
                `}
            </style>

            {/* Backdrop */}
            <div 
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 9998,
                    animation: 'fadeIn 0.2s ease-out',
                }}
                onClick={onClose}
            />

            {/* Modal/Drawer - Fixed to bottom */}
            <div 
                style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: '#ffffff',
                    borderTopLeftRadius: '20px',
                    borderTopRightRadius: '20px',
                    zIndex: 9999,
                    boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    animation: 'slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    maxHeight: '90vh',
                    minHeight: '50vh',
                    height: 'auto',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Drag Handle */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '12px 0 8px 0',
                    flexShrink: 0,
                    cursor: 'grab',
                }}>
                    <div style={{
                        width: '36px',
                        height: '4px',
                        borderRadius: '2px',
                        backgroundColor: '#d1d5db',
                    }} />
                </div>

                {/* Header */}
                <div style={{
                    padding: '0 20px 16px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                    borderBottom: '1px solid #f3f4f6',
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                    }}>
                        <h3 style={{
                            margin: 0,
                            fontSize: '20px',
                            fontWeight: '700',
                            color: '#111827',
                            letterSpacing: '-0.01em',
                        }}>{title}</h3>
                        {localSelected.length > 0 && (
                            <span style={{
                                background: 'linear-gradient(135deg, #00ca99, #00b8d4)',
                                color: '#ffffff',
                                fontSize: '12px',
                                fontWeight: '700',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                minWidth: '24px',
                                textAlign: 'center',
                            }}>
                                {localSelected.length}
                            </span>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            border: 'none',
                            background: '#f9fafb',
                            borderRadius: '50%',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                        }}
                        onMouseDown={(e) => {
                            e.currentTarget.style.background = '#f3f4f6';
                        }}
                        onMouseUp={(e) => {
                            e.currentTarget.style.background = '#f9fafb';
                        }}
                    >
                        <FaTimes style={{ color: '#6b7280', fontSize: '16px' }} />
                    </button>
                </div>

                {/* Content - Scrollable */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    padding: '20px 16px',
                    WebkitOverflowScrolling: 'touch',
                    minHeight: 0,
                }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                        gap: '16px',
                        width: '100%',
                    }}>
                        {items.map((item) => {
                            const isSelected = localSelected.includes(
                                type === "topics" ? item.name : item.id
                            );
                            const itemId = type === "topics" ? item.name : item.id;

                            return (
                                <div
                                    key={itemId}
                                    onClick={() => toggleSelection(itemId)}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        padding: '16px 12px',
                                        borderRadius: '16px',
                                        border: isSelected 
                                            ? '2px solid #00ca99' 
                                            : '1.5px solid #e5e7eb',
                                        background: isSelected 
                                            ? 'linear-gradient(135deg, rgba(0,202,153,0.1) 0%, rgba(0,184,212,0.08) 100%)'
                                            : '#ffffff',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        position: 'relative',
                                        boxShadow: isSelected 
                                            ? '0 4px 12px rgba(0,202,153,0.25)'
                                            : '0 1px 3px rgba(0,0,0,0.08)',
                                        transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                                    }}
                                >
                                    {isSelected && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '8px',
                                            right: '8px',
                                            width: '22px',
                                            height: '22px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #00ca99, #00b8d4)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 2px 6px rgba(0,202,153,0.4)',
                                        }}>
                                            <FaCheck style={{ 
                                                color: '#ffffff', 
                                                fontSize: '11px' 
                                            }} />
                                        </div>
                                    )}

                                    {type === "topics" ? (
                                        // Topic Icon
                                        <div style={{
                                            width: '56px',
                                            height: '56px',
                                            borderRadius: '50%',
                                            background: isSelected
                                                ? 'linear-gradient(135deg, #00ca99 0%, #00b8d4 50%, #7c4dff 100%)'
                                                : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                                            border: isSelected ? 'none' : '1.5px solid #e5e7eb',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginBottom: '12px',
                                            transition: 'all 0.2s',
                                            boxShadow: isSelected 
                                                ? '0 4px 12px rgba(0,202,153,0.3)'
                                                : '0 2px 4px rgba(0,0,0,0.05)',
                                        }}>
                                            <div style={{
                                                fontSize: '24px',
                                                color: isSelected ? '#ffffff' : '#00ca99',
                                            }}>
                                                {item.icon}
                                            </div>
                                        </div>
                                    ) : (
                                        // Space Avatar
                                        <div style={{
                                            width: '56px',
                                            height: '56px',
                                            borderRadius: '50%',
                                            marginBottom: '12px',
                                            backgroundImage: item.image_url 
                                                ? `url(${item.image_url})` 
                                                : 'none',
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            backgroundColor: item.image_url 
                                                ? 'transparent' 
                                                : item.color || '#00ca99',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#ffffff',
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            border: isSelected ? '3px solid #00ca99' : '2px solid transparent',
                                            boxShadow: isSelected 
                                                ? '0 0 0 4px rgba(0,202,153,0.15)'
                                                : '0 2px 4px rgba(0,0,0,0.1)',
                                            transition: 'all 0.2s',
                                        }}>
                                            {!item.image_url && item.initials}
                                        </div>
                                    )}

                                    <span style={{
                                        fontSize: '12px',
                                        fontWeight: isSelected ? '700' : '600',
                                        color: isSelected ? '#00ca99' : '#374151',
                                        textAlign: 'center',
                                        lineHeight: '1.4',
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        wordBreak: 'break-word',
                                    }}>
                                        {type === "topics" ? item.name : item.name}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Footer - Action Buttons */}
                {localSelected.length > 0 && (
                    <div style={{
                        padding: '16px 20px',
                        paddingBottom: `max(16px, calc(env(safe-area-inset-bottom) + 16px))`,
                        borderTop: '1px solid #f3f4f6',
                        display: 'flex',
                        gap: '12px',
                        flexShrink: 0,
                        background: '#ffffff',
                        boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
                    }}>
                        <button
                            onClick={handleClear}
                            style={{
                                flex: 1,
                                padding: '14px',
                                borderRadius: '12px',
                                border: '1.5px solid #e5e7eb',
                                background: '#ffffff',
                                color: '#6b7280',
                                fontSize: '15px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                            }}
                            onMouseDown={(e) => {
                                e.currentTarget.style.background = '#f9fafb';
                                e.currentTarget.style.transform = 'scale(0.98)';
                            }}
                            onMouseUp={(e) => {
                                e.currentTarget.style.background = '#ffffff';
                                e.currentTarget.style.transform = 'scale(1)';
                            }}
                        >
                            Clear All
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default MobileFilterView;
